// Demand projection, load uncertainty and metered profile import.
// Rendered on the Feasibility > Technical page inside the Demand tab.
//
// These are dispatch inputs: growth and uncertainty cause the engine to
// re-simulate the system at other load levels, because savings do not scale
// linearly with consumption.
import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getRechartsTooltipProps } from "../theme";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  GROWTH_HELPER_TEXT,
  REDISPATCH_GRANULARITY_OPTIONS,
  STEPS_HELPER_TEXT,
  UNCERTAINTY_HELPER_TEXT,
  annualLoadFactors,
  demandProjectionWarnings,
  profileDailyShape,
  validateDemandProjection,
} from "./config/demandProjection";
import { useLanguage } from "../context/LanguageContext";
import MeteredProfileImport from "./MeteredProfileImport";

// Greek for the projection section. The English originals stay in
// config/demandProjection.js; these are rendered directly in Greek mode
// instead of going through the generic catalogue, whose wording for this
// section was literal («Βήμα Αλλαγές», «Έτη αγκύρωσης»).
const GREEK_TEXT = {
  growthHint:
    "Αναμενόμενη ετήσια μεταβολή της κατανάλωσης, σωρευτικά σε όλη την περίοδο αξιολόγησης. " +
    "Αφήστε κενό ή 0 για σταθερή κατανάλωση. Είναι είσοδος της κατανομής και όχι απλός " +
    "πολλαπλασιαστής της αναφοράς: το σύστημα προσομοιώνεται ξανά στα υψηλότερα επίπεδα φορτίου, " +
    "ώστε να φαίνονται και ο κορεσμός της αυτοκατανάλωσης και η αύξηση της χρέωσης ισχύος.",
  uncertaintyHint:
    "Πόσο μπορεί να διαφέρει η πραγματική κατανάλωση από αυτή που δηλώθηκε. Το σύστημα " +
    "προσομοιώνεται και στα δύο άκρα του εύρους, δίνοντας εξοικονόμηση P90 (χαμηλό φορτίο) και " +
    "P10 (υψηλό φορτίο) δίπλα στο βασικό σενάριο. Αφήστε κενό για ένα μόνο, ντετερμινιστικό αποτέλεσμα.",
  stepsHint:
    "Μόνιμες αλλαγές στην κατανάλωση από ένα έτος και μετά: νέα γραμμή παραγωγής, επέκταση, " +
    "αλλαγή βαρδιών ή ηλεκτροδότηση θέρμανσης ή οχημάτων. Εφαρμόζονται επιπλέον της ετήσιας μεταβολής.",
  redispatch: {
    anchors: {
      label: "Επιλεγμένα έτη (συνιστάται)",
      description:
        "Επαναϋπολογισμός της κατανομής στα έτη 1, 5, 10, 15 και 20, με παρεμβολή για τα ενδιάμεσα έτη. " +
        "Αποτυπώνει την καμπύλη κορεσμού σε κλάσμα του χρόνου εκτέλεσης.",
    },
    all: {
      label: "Κάθε έτος",
      description:
        "Επαναϋπολογισμός και των είκοσι ετών. Η πιο ακριβής επιλογή, αλλά πολύ πιο αργή: " +
        "κάθε έτος είναι πλήρης βελτιστοποίηση 365 ημερών.",
    },
    none: {
      label: "Σταθερό φορτίο",
      description:
        "Καμία αύξηση της κατανάλωσης· κλιμακώνεται μόνο η τιμή λιανικής. Η συντηρητική επιλογή " +
        "όταν η μελλοντική κατανάλωση δεν είναι γνωστή.",
    },
  },
};

const hint = (title) => (
  <Tooltip title={title} arrow>
    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", ml: 0.5 }} />
  </Tooltip>
);

export default function DemandProjectionSection({
  projection,
  onChange,
  loadProfile,
  onLoadProfileChange,
  cfg,
  section = "all",
  showSourceSwitch = true,
  declaredAnnualKwh = 0,
}) {
  const { isGreek } = useLanguage();
  const validation = validateDemandProjection(projection, cfg);
  const warnings = demandProjectionWarnings(projection, cfg);
  const horizon = Number(cfg?.feasibility?.settlementPeriod) || 0;

  const patch = (partial) => onChange({ ...projection, ...partial });
  const numberOrBlank = (raw) => (raw === "" ? "" : Number(raw));
  const steps = projection.steps || [];

  const patchStep = (index, partial) =>
    patch({ steps: steps.map((s, i) => (i === index ? { ...s, ...partial } : s)) });
  const addStep = () =>
    patch({ steps: [...steps, { year: Math.min(3, horizon || 3), deltaPct: 10, label: "" }] });
  const removeStep = (index) =>
    patch({ steps: steps.filter((_, i) => i !== index) });

  const factors = annualLoadFactors(horizon, projection.growthPctYear, steps);
  const finalFactor = factors.length ? factors[factors.length - 1] : 1;

  // Two things that used to be one. A file is STORED once imported and stays
  // so until removed; it is IN USE only while the user has it selected as the
  // load source. Before the flag, the only way back to the modelled load was to
  // delete the file, and the only way back to the file was to import it again.
  const profileStored = Boolean(loadProfile?.values?.length);
  const profileActive = profileStored && loadProfile?.use !== false;
  const setProfileUse = (use) => {
    if (!profileStored) return;
    onLoadProfileChange({ ...loadProfile, use });
  };

  // A year of numbers tells you almost nothing by reading it. The mean day does:
  // a plausible site has a night floor, a morning ramp and an afternoon peak, and
  // the two commonest import faults -- a file in kW rather than kWh, and stamps
  // shifted by a whole day -- both show up here as a shape nobody recognises.
  const [inspectOpen, setInspectOpen] = useState(false);
  const [shapeView, setShapeView] = useState("year");
  const theme = useTheme();
  const dailyShape = useMemo(
    () => (profileStored && inspectOpen ? profileDailyShape(loadProfile) : null),
    [profileStored, inspectOpen, loadProfile]
  );
  const SEASON_LABELS = isGreek
    ? { winter: "Χειμώνας", spring: "Άνοιξη", summer: "Καλοκαίρι", autumn: "Φθινόπωρο" }
    : { winter: "Winter", spring: "Spring", summer: "Summer", autumn: "Autumn" };
  const SEASON_COLORS = {
    winter: "#2563eb",
    spring: "#16a34a",
    summer: "#f59e0b",
    autumn: "#b45309",
  };
  const shapeRows = useMemo(() => {
    if (!dailyShape) return [];
    return dailyShape.hours.map((hour, i) => {
      const row = { hour: `${String(hour).padStart(2, "0")}:00` };
      if (dailyShape.year) row.year = dailyShape.year[i];
      dailyShape.seasons.forEach((season) => {
        if (season.values) row[season.key] = season.values[i];
      });
      return row;
    });
  }, [dailyShape]);
  // Only the seasons the file actually covers; a missing one is absent, not zero.
  const presentSeasons = (dailyShape?.seasons || []).filter((s) => s.values);

  return (
    <Box sx={{ mt: section === "import" ? 0 : 3 }}>
      {section !== "projection" && (
      <>
      {/* ------------------ Metered profile import ------------------ */}
      <MeteredProfileImport
        loadProfile={loadProfile}
        onLoadProfileChange={onLoadProfileChange}
        cfg={cfg}
        declaredAnnualKwh={declaredAnnualKwh}
        showSourceNote={showSourceSwitch}
      />
      {profileStored && (
        <Box sx={{ mb: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
          <Button
            fullWidth
            onClick={() => setInspectOpen((open) => !open)}
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: inspectOpen ? "rotate(180deg)" : "none",
                  transition: "transform 150ms ease",
                }}
              />
            }
            sx={{ justifyContent: "space-between", textTransform: "none", fontWeight: 700, px: 1.5, py: 1 }}
          >
            Inspect profile
          </Button>
          <Collapse in={inspectOpen} unmountOnExit>
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
                <span>
                  The mean day of the imported file, in kWh per hour. Check the shape against
                  what the site actually does — a file in kW rather than kWh, or stamps shifted
                  by a day, shows up here and nowhere else.
                </span>
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={shapeView}
                onChange={(e, next) => next && setShapeView(next)}
                sx={{ mb: 1 }}
              >
                <ToggleButton value="year" sx={{ textTransform: "none" }}>
                  Whole year
                </ToggleButton>
                <ToggleButton value="seasons" sx={{ textTransform: "none" }} disabled={!presentSeasons.length}>
                  By season
                </ToggleButton>
              </ToggleButtonGroup>
              {shapeRows.length ? (
                <Box sx={{ height: 240 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={shapeRows} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                      <XAxis dataKey="hour" tick={{ fontSize: 11 }} interval={2} />
                      <YAxis tick={{ fontSize: 11 }} width={56} />
                      <RechartsTooltip
                        {...getRechartsTooltipProps(theme.palette.mode)}
                        formatter={(value) => `${Math.round(Number(value) * 10) / 10} kWh`}
                      />
                      {shapeView === "seasons" && <Legend wrapperStyle={{ fontSize: 11 }} />}
                      {shapeView === "year" ? (
                        <Line
                          type="monotone"
                          dataKey="year"
                          name={isGreek ? "Μέση ημέρα" : "Mean day"}
                          stroke={theme.palette.primary.main}
                          strokeWidth={2}
                          dot={false}
                        />
                      ) : (
                        presentSeasons.map((season) => (
                          <Line
                            key={season.key}
                            type="monotone"
                            dataKey={season.key}
                            name={SEASON_LABELS[season.key]}
                            stroke={SEASON_COLORS[season.key]}
                            strokeWidth={2}
                            dot={false}
                          />
                        ))
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  <span>The file is shorter than a day, so there is no mean day to draw.</span>
                </Typography>
              )}
              {shapeView === "seasons" && presentSeasons.length < 4 && (
                <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "warning.main" }}>
                  <span>
                    The file does not cover every season. The missing ones are left out rather
                    than drawn as zero.
                  </span>
                </Typography>
              )}
            </Box>
          </Collapse>
        </Box>
      )}

      {profileStored && showSourceSwitch && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}>
            Load source
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={profileActive ? "measured" : "modelled"}
            onChange={(e, next) => next && setProfileUse(next === "measured")}
          >
            <ToggleButton value="measured" sx={{ textTransform: "none" }}>
              Measured profile
            </ToggleButton>
            <ToggleButton value="modelled" sx={{ textTransform: "none" }}>
              Modelled from inputs
            </ToggleButton>
          </ToggleButtonGroup>
          <Alert severity="info" sx={{ mt: 1 }}>
            {profileActive ? (
              <span>
                The measured profile replaces the modelled one entirely — the monthly
                consumption, operating hours and load-shape weights above are not read.
                The file stays here either way; switch to model from the inputs without
                losing it.
              </span>
            ) : (
              <span>
                The load is modelled from the monthly consumption, operating hours and
                load-shape weights above. The imported file is kept and can be inspected,
                but the engine does not read it until it is selected again.
              </span>
            )}
          </Alert>
        </Box>
      )}
      </>
      )}

      {section !== "import" && (
      <>
      {/* ------------------ Growth ------------------ */}
      {/* One heading, one (i): the tooltip explains both growth and the
          uncertainty band, so the two no longer sit side by side. */}
      <Box sx={{ display: "flex", alignItems: "center", mt: 3, mb: 1.5, borderLeft: "4px solid", borderColor: "primary.main", pl: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
          {isGreek ? "Μεταβολή και αβεβαιότητα κατανάλωσης" : "Consumption growth & uncertainty"}
        </Typography>
        {hint(
          <Box data-no-translate="">
            <Typography variant="caption" sx={{ display: "block", mb: 0.75 }}>
              {isGreek ? GREEK_TEXT.growthHint : GROWTH_HELPER_TEXT}
            </Typography>
            <Typography variant="caption" sx={{ display: "block" }}>
              {isGreek ? GREEK_TEXT.uncertaintyHint : UNCERTAINTY_HELPER_TEXT}
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1.4fr" },
          gap: 2,
        }}
      >
        <TextField
          id="field-demand-growth"
          fullWidth
          size="small"
          label={isGreek ? "Αύξηση φορτίου (%/έτος)" : "Load growth (%/year)"}
          type="number"
          value={projection.growthPctYear ?? ""}
          onChange={(e) => patch({ growthPctYear: numberOrBlank(e.target.value) })}
          error={Boolean(validation.growthPctYear)}
          helperText={
            validation.growthPctYear ||
            (horizon && factors.length
              ? isGreek
                ? `Φορτίο έτους ${horizon}: ${Math.round(finalFactor * 100)}% του σημερινού`
                : `Year ${horizon} load: ${Math.round(finalFactor * 100)}% of today`
              : isGreek
                ? "Κενό ή 0: η κατανάλωση μένει σταθερή."
                : "Blank or 0 holds consumption flat.")
          }
          inputProps={{ step: "any" }}
        />
        <TextField
          fullWidth
          size="small"
          label={isGreek ? "Αβεβαιότητα φορτίου (± %)" : "Load uncertainty (+/- %)"}
          type="number"
          value={projection.uncertaintyPct ?? ""}
          onChange={(e) => patch({ uncertaintyPct: numberOrBlank(e.target.value) })}
          error={Boolean(validation.uncertaintyPct)}
          helperText={
            validation.uncertaintyPct ||
            (isGreek ? "Δίνει εύρος εξοικονόμησης P90 / P10." : "Produces a P90 / P10 savings band.")
          }
          inputProps={{ min: 0, max: 50, step: "any" }}
        />
        <FormControl fullWidth size="small">
          <InputLabel>{isGreek ? "Λεπτομέρεια επαναβελτιστοποίησης κατανομής" : "Re-dispatch detail"}</InputLabel>
          <Select
            label={isGreek ? "Λεπτομέρεια επαναβελτιστοποίησης κατανομής" : "Re-dispatch detail"}
            value={projection.redispatchGranularity}
            onChange={(e) => patch({ redispatchGranularity: e.target.value })}
          >
            {REDISPATCH_GRANULARITY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {isGreek ? GREEK_TEXT.redispatch[option.value]?.label || option.label : option.label}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>
            {(() => {
              const option = REDISPATCH_GRANULARITY_OPTIONS.find(
                (o) => o.value === projection.redispatchGranularity
              );
              if (!option) return "";
              return isGreek
                ? GREEK_TEXT.redispatch[option.value]?.description || option.description
                : option.description;
            })()}
          </FormHelperText>
        </FormControl>
      </Box>

      {/* ------------------ Step changes ------------------ */}
      <Box sx={{ display: "flex", alignItems: "center", mt: 3, mb: 1, borderLeft: "4px solid", borderColor: "primary.main", pl: 1.25 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "text.primary" }}>
          {isGreek ? "Μόνιμες μεταβολές κατανάλωσης" : "Step changes in consumption"}
        </Typography>
        {hint(isGreek ? GREEK_TEXT.stepsHint : STEPS_HELPER_TEXT)}
      </Box>
      {steps.length === 0 && (
        <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
          {isGreek
            ? "Καμία. Προσθέστε μία για γνωστή επέκταση, νέα γραμμή παραγωγής ή ηλεκτροδότηση θέρμανσης ή οχημάτων."
            : "None. Add one for a known expansion, a new line, or electrification of heat or vehicles."}
        </Typography>
      )}
      {steps.map((step, index) => (
        <Box
          key={index}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "110px 140px 1fr 44px" },
            gap: 1.5,
            alignItems: "start",
            mt: 1,
            mb: 1,
          }}
        >
          <TextField
            size="small"
            label={isGreek ? "Από το έτος" : "From year"}
            type="number"
            value={step.year ?? ""}
            onChange={(e) => patchStep(index, { year: numberOrBlank(e.target.value) })}
            error={Boolean(validation[`step_${index}_year`])}
            helperText={validation[`step_${index}_year`] || ""}
            inputProps={{ min: 1, max: horizon || undefined, step: 1 }}
          />
          <TextField
            size="small"
            label={isGreek ? "Μεταβολή (%)" : "Change (%)"}
            type="number"
            value={step.deltaPct ?? ""}
            onChange={(e) => patchStep(index, { deltaPct: numberOrBlank(e.target.value) })}
            error={Boolean(validation[`step_${index}_delta`])}
            helperText={validation[`step_${index}_delta`] || ""}
            inputProps={{ step: "any" }}
          />
          <TextField
            size="small"
            label={isGreek ? "Αιτία (εμφανίζεται στην αναφορά)" : "Reason (appears in the report)"}
            value={step.label ?? ""}
            onChange={(e) => patchStep(index, { label: e.target.value })}
          />
          <IconButton
            size="small"
            onClick={() => removeStep(index)}
            aria-label={isGreek ? "Αφαίρεση μεταβολής" : "Remove step"}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addStep} disabled={!horizon}>
        {isGreek ? "Προσθήκη μεταβολής" : "Add step change"}
      </Button>

      {warnings.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {warnings.map((message, i) => (
            <Typography key={i} variant="body2">{message}</Typography>
          ))}
        </Alert>
      )}
      </>
      )}
    </Box>
  );
}
